import {
  buildSearchBox,
  buildResultList,
  buildResultsPerPage,
  buildPager,
  buildBreadcrumbManager,
  buildQuerySummary,
  buildDateFilter,
  RangeFacetSortCriterion,
  buildTab,
  buildQueryExpression
} from '@coveo/headless';
import { headlessEngine, headlessEngineModal } from '../engine';
import { StringOperator } from '@coveo/headless/dist/definitions/utils/query-expression/common/operator';

export type filterFacetsProps = {
  title: string;
  field: string;
  resultsMustMatch?: 'allValues' | 'atLeastOneValue';
  defaultLabel: string;
}[];

export type facetLabelsProps = {
  title: string;
  field: string;
  range: boolean;
  rangeLabels: string[];
  rangeStars: string[];
  rangeValues: { start: number; end: number; endInclusive?: boolean }[];
  sortCriteria: string;
};

export type facetProps = {
  title: string;
  field: string;
  isCategory?: boolean;
  certificationsLabel?: string;
  specialtiesLabel?: string;
  customOrder?: {
    facetName: string;
    iconUrl: string;
  }[];
  colors?: { value: string; color_value: string }[];
  rangeLabels?: string[];
  rangeStars?: string[];
  rangeValues?: { start: number; end: number; endInclusive?: boolean }[];
  isWarrantiesLabel?: string;
  isPrice?: boolean;
  isColor?: boolean;
  range?: boolean;
  sortCriteria?: RangeFacetSortCriterion;
  resultsMustMatch?: 'allValues' | 'atLeastOneValue';
  hasDuplicate?: boolean;
  isDuplicate?: boolean;
};

export type facetsProps = facetProps[];

export type rangeFacetProps = {
  field: string;
  title: string;
  rangeLabels: string[];
  rangeStarLabels: string[];
};

export type coveoContextProps = {
  [key: string]: string;
};

export type tabPerFacetProps = {
  id: string;
  field: string;
  operator: StringOperator;
  values: string[];
};

export type filtersPerFacetValueProps = {
  facetField: string;
  facetValue: string;
  context?: {
    [key: string]: string;
  };
  tab?: tabPerFacetProps;
}[];

const wrapper = document.getElementsByClassName(
  'coveo-listing__data-wrapper'
)?.[0] as HTMLElement;
const dataset = wrapper && wrapper.dataset;

const modalWrapper = document.getElementsByClassName(
  'coveo-listing__data-wrapper-modal'
)[0] as HTMLElement;
const datasetModal = modalWrapper
  ? (modalWrapper as HTMLElement).dataset
  : null;

export const maxResultsPerPage = dataset?.resultsPerPage
  ? JSON.parse(dataset.resultsPerPage as string)
  : 10;

export const filterFacets = dataset?.filterFacets
  ? (JSON.parse(dataset.filterFacets as string) as filterFacetsProps)
  : [];
export const facets = dataset?.facets
  ? (JSON.parse(dataset.facets as string) as facetsProps)
  : [];
export const facetOptionsNumber = dataset?.facetOptionsNumber
  ? Number(dataset.facetOptionsNumber)
  : 999;
export const rangeFacet = dataset?.rangeFacet
  ? JSON.parse(dataset.rangeFacet as string)
  : ({} as rangeFacetProps);

export const facetsLabels = dataset?.facets
  ? JSON.parse(dataset.facets as string)
  : ({} as facetLabelsProps);

export const fieldsObject = dataset?.searchFields
  ? JSON.parse(dataset.searchFields as string)
  : {};
export const fieldsObjectModal = datasetModal?.searchFields
  ? JSON.parse((datasetModal as DOMStringMap).searchFields as string)
  : {};

export const fieldsToInclude =
  fieldsObject && (Object.values(fieldsObject) as string[]);

export const fieldsToIncludeModal =
  fieldsObjectModal && (Object.values(fieldsObjectModal) as string[]);

export const fieldsKeys = Object.keys(fieldsObject) as string[];

export const certificationsImagePath =
  dataset?.certificationsImagePath as string;

export const certificationImages = dataset?.certificationImages
  ? JSON.parse(dataset.certificationImages as string)
  : [];

export const certificationImagesResidential =
  dataset?.certificationImagesResidential
    ? JSON.parse(dataset.certificationImagesResidential as string)
    : certificationImages;

export const certificationImagesCommercial =
  dataset?.certificationImagesCommercial
    ? JSON.parse(dataset.certificationImagesCommercial as string)
    : certificationImages;

export const contractorImages = dataset?.contractorPlaceholderImages
  ? JSON.parse(dataset.contractorPlaceholderImages as string)
  : [];

export const imagesPlaceholdersObj = dataset?.imagePlaceholders
  ? JSON.parse(dataset.imagePlaceholders as string)
  : '';

export const labelsObj = dataset?.labels
  ? JSON.parse(dataset?.labels as string)
  : '';

export const templateTypes = dataset?.templateTypes
  ? JSON.parse(dataset.templateTypes as string)
  : [];

export const defaultQuery = dataset ? (dataset.defaultQuery as string) : null;

export const coveoContext = dataset?.coveoContext
  ? (JSON.parse(dataset.coveoContext as string) as coveoContextProps)
  : {};

export const coveoContextModal = datasetModal?.coveoContext
  ? (JSON.parse(datasetModal.coveoContext as string) as coveoContextProps)
  : {};

export const filtersPerFacetValue = dataset?.filtersPerFacetValue
  ? (JSON.parse(
      dataset.filtersPerFacetValue as string
    ) as filtersPerFacetValueProps)
  : [];

/*
  Usage:
  import * as Controllers from  '../../coveo/controllers/controllers';

  Then, in the component:
*/

const numberOfSuggestions = dataset?.numberOfSuggestions
  ? parseInt(dataset.numberOfSuggestions)
  : 5;

export const searchBox =
  headlessEngine &&
  buildSearchBox(headlessEngine, {
    options: {
      highlightOptions: {
        notMatchDelimiters: {
          open: '<strong>',
          close: '</strong>'
        },
        correctionDelimiters: {
          open: '<i>',
          close: '</i>'
        }
      },
      numberOfSuggestions: numberOfSuggestions
    }
  });

export const resultList =
  headlessEngine &&
  buildResultList(headlessEngine, {
    options: {
      fieldsToInclude
    }
  });

export const resultListModal =
  headlessEngineModal &&
  buildResultList(headlessEngineModal, {
    options: {
      fieldsToInclude: fieldsToIncludeModal
    }
  });

export const facetBreadcrumb =
  headlessEngine && buildBreadcrumbManager(headlessEngine);

export const resultsPerPage =
  headlessEngine &&
  buildResultsPerPage(headlessEngine, {
    initialState: { numberOfResults: maxResultsPerPage }
  });

/*
  Usage:
 <DateFilter controller={Controllers.dateFilter} />
*/
export const dateFilter =
  headlessEngine &&
  buildDateFilter(headlessEngine, {
    options: {
      field: 'date'
    }
  });

export const querySummary = headlessEngine && buildQuerySummary(headlessEngine);

export const pager =
  headlessEngine &&
  buildPager(headlessEngine, {
    options: { numberOfPages: 5 }
  });

const defaultMessageExpression = buildQueryExpression()
  .addStringField({
    field: 'gaf_content_type',
    operator: 'isExactly',
    negate: true,
    values: ['NO CONTENT TYPE FILTER']
  })
  .toQuerySyntax();

export const defaultTab =
  headlessEngine &&
  buildTab(headlessEngine, {
    initialState: { isActive: false },
    options: {
      id: 'defaultTab',
      expression: defaultMessageExpression
    }
  });
